import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./StudentSearchBar.module.sass";
import TextInput from "../TextInput";
import LetterAvatar from "../LetterAvatar";
import Icon from "../Icon";
import { searchTutors } from "../../services/tutor.service";

const TutorSearchBar = ({ 
  className,
  label = "Rechercher un tuteur",
  placeholder = "Nom, email ou téléphone (min. 2 caractères)",
  onSelectTutor,
  showDetails = true,
  required = false,
  excludeTutorIds = [] // IDs des tuteurs à exclure des résultats
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchTutors = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchTutors(searchQuery);
          // Filtrer les tuteurs à exclure
          const filteredResults = results.filter(tutor => !excludeTutorIds.includes(tutor.id));
          setSearchResults(filteredResults);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des tuteurs:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    
    fetchTutors();
  }, [searchQuery, excludeTutorIds]);

  // Gérer la sélection d'un tuteur
  const handleSelectTutor = (tutor) => {
    setSelectedTutor(tutor);
    setSearchQuery(`${tutor.first_name} ${tutor.last_name}`);
    setShowResults(false);
    
    // Transmettre le tuteur sélectionné au composant parent
    if (onSelectTutor) {
      onSelectTutor(tutor);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (selectedTutor && e.target.value !== `${selectedTutor.first_name} ${selectedTutor.last_name}`) {
      setSelectedTutor(null);
      if (onSelectTutor) {
        onSelectTutor(null);
      }
    }
  };

  // Réinitialiser la recherche
  const handleReset = () => {
    setSelectedTutor(null);
    setSearchQuery('');
    if (onSelectTutor) {
      onSelectTutor(null);
    }
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <TextInput
            className={styles.field}
            label={label}
            name="student-search"
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            required={required}
          />
          {searchQuery && (
            <button className={styles.clearButton} onClick={handleReset} type="button" aria-label="Effacer la recherche">
              <Icon name="close-circle" size="20" />
            </button>
          )}
        </div>
        
        {showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map(tutor => (
              <div 
                key={tutor.id} 
                className={styles.resultItem}
                onClick={() => handleSelectTutor(tutor)}
              >
                <div className={styles.resultContent}>
                  <div style={{ marginRight: '20px' }}>
                    <LetterAvatar
                      firstName={tutor.first_name}
                      lastName={tutor.last_name}
                      size={40}
                    />
                  </div>
                  <div className={styles.studentInfo}>
                    <div className={styles.studentName}>{tutor.first_name} {tutor.last_name}</div>
                    <div className={styles.studentDetails}>
                      <span>{tutor.email}</span>
                      <span>•</span>
                      <span>{tutor.phone}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !selectedTutor && (
          <div className={styles.noResults}>
            Aucun tuteur trouvé
          </div>
        )}
      </div>

      {selectedTutor && showDetails && (
        <div className={styles.selectedStudentDetails}>
          <h3>Détails du tuteur sélectionné</h3>
          <div className={styles.detailRow}>
            <span className={styles.label}>Nom:</span>
            <span className={styles.value}>{selectedTutor.first_name} {selectedTutor.last_name}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Email:</span>
            <span className={styles.value}>{selectedTutor.email || 'Non renseigné'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Téléphone:</span>
            <span className={styles.value}>{selectedTutor.phone || 'Non renseigné'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Date de naissance:</span>
            <span className={styles.value}>
              {selectedTutor.birth_date ? 
                new Date(selectedTutor.birth_date).toLocaleDateString('fr-FR') : 
                'Non renseignée'}
            </span>
          </div>
          {selectedTutor.job_title && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Poste:</span>
              <span className={styles.value}>{selectedTutor.job_title}</span>
            </div>
          )}
          {selectedTutor.highest_degree_title && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Diplôme:</span>
              <span className={styles.value}>{selectedTutor.highest_degree_title}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TutorSearchBar;
